import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import GameDetailsHeader from 'src/components/GameDetailsHeader';
import Header from 'src/components/Header';
import { ArrowLeft, CloseIcon } from 'src/components/icons';
import { MEDIUM_BREAKPOINT } from 'src/components/modular-grid/constants';
import Overlay from 'src/components/Overlay';
import Portal from 'src/components/Portal';
import { useWindowSize } from 'src/hooks/useWindowSize';
import styles from './Modal.module.scss';

interface ModalPropTypes {
  componentVisible?: boolean;
  onModalOverlayClick?: () => void;
  onModalBackButtonClick?: () => void;
  children?: React.ReactNode;
  backButtonVisible?: boolean;
  title?: string;
  backButtonText?: string;
  sidebarBackButtonVisible?: boolean;
}

const Modal: React.FC<ModalPropTypes> = props => {
  let {
    componentVisible = false,
    children,
    onModalOverlayClick,
    onModalBackButtonClick,
    backButtonVisible,
    title,
    backButtonText,
    sidebarBackButtonVisible = true,
  } = props;
  const width = useWindowSize().width;

  const [shouldRender, setRender] = useState<boolean>(false);
  useEffect(() => {
    setRender(componentVisible);
  }, [componentVisible]);
  if (!children) return null;
  if (!shouldRender) return null;

  const onAnimationEnd = () => {
    if (!componentVisible) setRender(false);
  };

  function handleClick() {
    onModalOverlayClick && onModalOverlayClick();
  }

  function handleBackClick() {
    onModalBackButtonClick && onModalBackButtonClick();
  }

  const size = width < MEDIUM_BREAKPOINT.min ? 'small' : 'wide';

  const SmallModalCmp = (
    <div data-testid="modal-small" id="modal" className={styles.wrapperSmall}>
      {backButtonVisible && backButtonText && (
        <GameDetailsHeader
          backButtonText={backButtonText}
          onBackButtonClick={handleBackClick}
        />
      )}
      {title && !backButtonVisible && (
        <Header
          title={title}
          backButtonHidden={true}
          backButtonText={backButtonText || ''}
          onBackButtonClick={handleBackClick}
        />
      )}
      <div>{children}</div>
    </div>
  );

  const LargeModalCmp = (
    <>
      <div
        data-testid="modal-large"
        id="modal"
        className={cx(
          styles.sidebar,
          componentVisible ? styles.show : styles.hide
        )}
        onAnimationEnd={onAnimationEnd}
      >
        <div
          className={cx(
            styles.sidebarHeader,
            sidebarBackButtonVisible &&
              backButtonVisible &&
              styles.backButtonVisible
          )}
        >
          <div
            data-testid="sidebar-back"
            className={styles.backButton}
            onClick={handleBackClick}
          >
            <ArrowLeft />
          </div>
          {title && <p>{title}</p>}
          <div
            data-testid="sidebar-close"
            className={styles.closeIcon}
            onClick={handleClick}
          >
            <CloseIcon />
          </div>
        </div>
        <div className={styles.sidebarContent}>{children}</div>
      </div>
      <Overlay
        componentVisible={componentVisible}
        onClick={onModalOverlayClick}
      />
    </>
  );

  return shouldRender ? (
    <Portal
      id={size === 'small' ? 'contentWrapper' : 'page-content'}
      children={size === 'small' ? SmallModalCmp : LargeModalCmp}
      childId="modal"
    />
  ) : null;
};

export default Modal;
